/* eslint-disable @next/next/no-img-element */
"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { lazy, Suspense, useEffect, useRef, useState, type MouseEvent } from "react";
import type { AuthDialogMode, RegistrationRole } from "@/modules/auth/components/AuthDialog";
import { CarevincoLogo } from "@/shared/components/CarevincoLogo";
import { coreServices } from "@/shared/config/coreServices";
import { isSiteActive } from "@/shared/config/customerSupport";
import { useAuthStore } from "@/shared/store/authStore";

const AuthenticatedLandingHeader = lazy(() =>
  import("./AuthenticatedLandingHeader").then((module) => ({ default: module.AuthenticatedLandingHeader })),
);

type LandingHeaderProps = {
  authDialogOpen?: boolean;
  onAuthRequest: (mode: AuthDialogMode, role?: RegistrationRole) => void;
};

const FIGMA_ASSETS = {
  portalIcon: "/assets/landingHeader/portal-icon-e066ad3f-9b90-4373-be93-cecb5fa71eec.png",
};
const PROVIDER_PAGE_HREF = "/for-providers";

type LockedDropdown = "services" | null;

function scrollToTarget(hash: string) {
  const targetId = hash.replace("#", "");
  const target = document.getElementById(targetId);

  if (!target) {
    return;
  }

  const header = document.querySelector(".cv-wireframe .site-header") as HTMLElement | null;
  const headerOffset = header?.getBoundingClientRect().height ?? 0;
  const targetTop = window.scrollY + target.getBoundingClientRect().top - headerOffset - 10;

  window.history.pushState(null, "", hash);
  window.scrollTo({ top: Math.max(targetTop, 0), behavior: "smooth" });
}

function scrollToHash(event: MouseEvent<HTMLAnchorElement>, href: string) {
  const hashIndex = href.indexOf("#");

  if (hashIndex < 0 || typeof window === "undefined") {
    return;
  }

  const hash = href.slice(hashIndex);
  const target = document.getElementById(hash.replace("#", ""));
  const canScrollOnCurrentPage = href.startsWith("#") || window.location.pathname === "/";

  if (target && canScrollOnCurrentPage) {
    event.preventDefault();
    scrollToTarget(hash);
  }
}

export function LandingHeader({ authDialogOpen = false, onAuthRequest }: LandingHeaderProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const pathname = usePathname();
  const activeSection = pathname?.startsWith("/services")
    ? "services"
    : pathname?.startsWith("/terms-and-conditions")
      ? "terms"
      : pathname?.startsWith("/privacy-policy")
        ? "privacy"
        : pathname?.startsWith("/account-deletion")
          ? "account-deletion"
          : "";
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [lockedDropdown, setLockedDropdown] = useState<LockedDropdown>(null);
  const headerInnerRef = useRef<HTMLDivElement | null>(null);
  const mobileNavRef = useRef<HTMLElement | null>(null);
  const mobileNavTriggerRef = useRef<HTMLButtonElement | null>(null);
  const isProviderPage = pathname?.startsWith(PROVIDER_PAGE_HREF) ?? false;
  const printTitle = activeSection === "terms"
    ? "Terms & Conditions | Carevinco"
    : activeSection === "privacy"
      ? "Privacy Policy | Carevinco"
      : activeSection === "account-deletion"
        ? "Delete or Deactivate Your Account | Carevinco"
        : "";

  useEffect(() => {
    if (pathname !== "/" || typeof window === "undefined" || !window.location.hash) {
      return undefined;
    }

    const hash = window.location.hash;
    const targetId = hash.replace("#", "");
    let frame = 0;
    let observer: MutationObserver | null = null;

    function scrollWhenReady() {
      if (!document.getElementById(targetId)) {
        return false;
      }

      frame = window.requestAnimationFrame(() => scrollToTarget(hash));
      return true;
    }

    if (!scrollWhenReady()) {
      observer = new MutationObserver(() => {
        if (scrollWhenReady()) {
          observer?.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      observer?.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [pathname]);

  useEffect(() => {
    function handleDocumentClick(event: globalThis.MouseEvent) {
      if (!lockedDropdown) {
        return;
      }

      const target = event.target as Node | null;

      if (target && headerInnerRef.current?.contains(target)) {
        return;
      }

      setLockedDropdown(null);
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setLockedDropdown(null);
        setIsMobileNavOpen(false);
      }
    }

    document.addEventListener("click", handleDocumentClick);
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [lockedDropdown]);

  useEffect(() => {
    if (!isMobileNavOpen || typeof document === "undefined") {
      return undefined;
    }

    function handleOutsidePointerDown(event: PointerEvent) {
      const target = event.target as Node | null;

      if (
        !target ||
        mobileNavRef.current?.contains(target) ||
        mobileNavTriggerRef.current?.contains(target)
      ) {
        return;
      }

      setIsMobileNavOpen(false);
    }

    document.addEventListener("pointerdown", handleOutsidePointerDown);

    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, [isMobileNavOpen]);

  if (isAuthenticated) {
    return (
      <Suspense fallback={null}>
        <AuthenticatedLandingHeader onAuthRequest={onAuthRequest} />
      </Suspense>
    );
  }

  function closeAllNavigation() {
    setIsMobileNavOpen(false);
    setLockedDropdown(null);
  }

  function closeMobileNav() {
    setIsMobileNavOpen(false);
  }

  function handleSectionClick(event: MouseEvent<HTMLAnchorElement>, href: string) {
    scrollToHash(event, href);
    closeAllNavigation();
  }

  function handleAuthClick() {
    closeAllNavigation();
    onAuthRequest("login");
  }

  function toggleDropdown(name: Exclude<LockedDropdown, null>) {
    setLockedDropdown((current) => (current === name ? null : name));
  }

  function navClass(sectionId: string, extraClass = "") {
    const isActive = pathname === "/" ? sectionId === "home" : activeSection === sectionId;
    const classes = [extraClass, isActive ? "is-active" : ""].filter(Boolean);
    return classes.join(" ") || undefined;
  }

  const serviceLinks = coreServices.map((service) => (
    <Link href={`/services/${service.slug}`} key={service.slug} role="menuitem" onClick={closeAllNavigation}>
      <strong>{service.title}</strong>
      {/* <span>{service.summary}</span> */}
    </Link>
  ));

  return (
    <header className="site-header">
      <div className="site-header__inner" ref={headerInnerRef}>
        <Link className="brand" href="/#home" onClick={(event) => handleSectionClick(event, "/#home")}>
          <CarevincoLogo header priority />
        </Link>
        {printTitle ? <span className="site-header__print-title">{printTitle}</span> : null}
        <button
          aria-controls="carevinco-mobile-nav"
          aria-expanded={isMobileNavOpen}
          aria-label={isMobileNavOpen ? "Close navigation menu" : "Open navigation menu"}
          className="wireframe-nav-mobile-trigger"
          ref={mobileNavTriggerRef}
          type="button"
          onClick={() => setIsMobileNavOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>
        <nav className="main-nav" aria-label="Primary navigation">
          <Link className={navClass("home")} href="/#home" onClick={(event) => handleSectionClick(event, "/#home")}>
            Home
          </Link>

          <div className={`main-nav__dropdown${lockedDropdown === "services" ? " is-menu-locked" : ""}`}>
            <button
              aria-expanded={lockedDropdown === "services"}
              aria-haspopup="menu"
              className={navClass("services", "main-nav__dropdown-trigger")}
              type="button"
              onClick={() => toggleDropdown("services")}
            >
              Services <span className="main-nav__dropdown-chevron" aria-hidden="true" />
            </button>
            <div className="main-nav__dropdown-menu" role="menu" aria-label="Carevinco services">
              {serviceLinks}
            </div>
          </div>
          <Link aria-current={isProviderPage ? "page" : undefined} className={isProviderPage ? "is-active" : undefined} href={PROVIDER_PAGE_HREF} onClick={closeAllNavigation}>
            For Providers
          </Link>
          <Link className={navClass("contact")} href="#contact" onClick={(event) => handleSectionClick(event, "#contact")}>
            Contact Us
          </Link>
          {isSiteActive ? (
            <button aria-expanded={authDialogOpen} aria-haspopup="dialog" className="portal-btn portal-btn--auth" type="button" onClick={handleAuthClick}>
              <img src={FIGMA_ASSETS.portalIcon} alt="" height={24} width={24} />
              Get Started
            </button>
          ) : null}
        </nav>
      </div>
      <div aria-hidden={!isMobileNavOpen} className={`wireframe-nav-mobile-backdrop${isMobileNavOpen ? " is-open" : ""}`} onClick={closeMobileNav} />
      <nav
        aria-label="Mobile primary navigation"
        className={`wireframe-mobile-nav${isMobileNavOpen ? " is-open" : ""}`}
        id="carevinco-mobile-nav"
        ref={mobileNavRef}
      >
        <div className="wireframe-mobile-nav__head">
          <span>Menu</span>
          <button aria-label="Close navigation menu" type="button" onClick={closeMobileNav}>×</button>
        </div>
        <Link className={navClass("home")} href="/#home" onClick={(event) => handleSectionClick(event, "/#home")}>
          Home
        </Link>
        <details className={`wireframe-mobile-nav__services${activeSection === "services" ? " is-active" : ""}`}>
          <summary>Services <span aria-hidden="true" /></summary>
          <div>{serviceLinks}</div>
        </details>
        <Link aria-current={isProviderPage ? "page" : undefined} className={isProviderPage ? "is-active" : undefined} href={PROVIDER_PAGE_HREF} onClick={closeAllNavigation}>
          For Providers
        </Link>
        <Link className={navClass("contact")} href="#contact" onClick={(event) => handleSectionClick(event, "#contact")}>
          Contact Us
        </Link>
        {isSiteActive ? (
          <button aria-expanded={authDialogOpen} aria-haspopup="dialog" className="portal-btn portal-btn--auth" type="button" onClick={handleAuthClick}>
            <img src={FIGMA_ASSETS.portalIcon} alt="" height={24} width={24} />
            Get Started
          </button>
        ) : null}
      </nav>
    </header>
  );
}
